import { authStorage } from "../storage/auth-storage";
import type { AuthSession } from "../types/auth.types";
import type { BaseUserResponse } from "../../data/base-user-response.data";

export interface AuthSessionState {
  user: BaseUserResponse | null;
  token: string | null;
  refreshToken: string | null;
  merchantOnboardingStage: AuthSession["merchantOnboardingStage"];
  isLoggedIn: boolean;
  isInitialized: boolean;
}

const createSnapshot = (): AuthSessionState => {
  const session = typeof window === "undefined" ? null : authStorage.load();

  return {
    user: session?.user ?? null,
    token: session?.token ?? null,
    refreshToken: session?.refreshToken ?? null,
    merchantOnboardingStage: session?.merchantOnboardingStage,
    isLoggedIn: !!session?.user,
    isInitialized: true,
  };
};

let snapshot: AuthSessionState = createSnapshot();

const updateSnapshot = (next: Partial<AuthSessionState>) => {
  snapshot = { ...snapshot, ...next };
};

export const authSessionState = {
  getSnapshot: () => snapshot,

  initialize() {
    snapshot = createSnapshot();
  },

  setSession(session: AuthSession) {
    authStorage.save(session);
    updateSnapshot({
      user: session.user,
      token: session.token,
      refreshToken: session.refreshToken,
      merchantOnboardingStage: session.merchantOnboardingStage,
      isLoggedIn: true,
      isInitialized: true,
    });
  },

  clearSession() {
    authStorage.clear();
    updateSnapshot({
      user: null,
      token: null,
      refreshToken: null,
      merchantOnboardingStage: undefined,
      isLoggedIn: false,
      isInitialized: true,
    });
  },

  updateUser(user: BaseUserResponse) {
    authStorage.updateUser(user);
    updateSnapshot({
      user,
      isLoggedIn: true,
      isInitialized: true,
    });
  },

  updateTokens(token: string, refreshToken: string) {
    const current = authStorage.load();
    if (!current) return;

    authStorage.save({ ...current, token, refreshToken });
    updateSnapshot({ token, refreshToken, isLoggedIn: true, isInitialized: true });
  },
};
